import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import {
  IconBike,
  IconPalette,
  IconWorld,
  IconTrophy,
  IconUser,
  IconDiamond,
  IconMap2,
  IconCompass,
  IconPlus,
  IconSettings,
  IconShieldCog
} from '@tabler/icons-react';
import './NavigationBar.css';
import MythicLogo from '../assets/logos/80ies_TdF_Logo.png';
import StableAvatar from './StableAvatar';
import { db, functions } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import DiamondsPanel from './DiamondsPanel';
import { useAdminView } from '../contexts/AdminViewContext';

const CREATE_PAGE_REFRESH_ACTIVITIES_EVENT = 'mythicmaps:create:refresh-activities';

// Auto-open the diamonds panel once for brand-new signups (generous window so it
// survives clock skew / the OAuth redirect). Mirrors HomePage's brand-new check.
const BRAND_NEW_SUPPRESS_MS = 24 * 60 * 60 * 1000;
const DIAMOND_PANEL_AUTOOPEN_KEY = (uid) => `mythicmaps:diamondPanelAutoOpened:${uid}`;

// Below this many remaining diamonds the nav badge turns amber.
const LOW_BALANCE_THRESHOLD = 3;

const NavigationBar = ({ user, onLoginRequired, userCredits, isLoadingCredits, onCreditsUpdated }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { isAdmin } = useAdminView();
  const [creditAnimation, setCreditAnimation] = useState(false);
  const [exploreMenuOpen, setExploreMenuOpen] = useState(false);
  const [diamondPanelOpen, setDiamondPanelOpen] = useState(false);
  const [realUnclaimed, setRealUnclaimed] = useState(false);
  // Prefetched getCreditRewardStatus result (null while loading). Shared with the
  // DiamondsPanel so opening it renders the claim cards instantly, no second fetch.
  const [rewardStatus, setRewardStatus] = useState(null);
  const previousCredits = useRef(userCredits);
  const autoOpenCheckedRef = useRef(false);
  const hoverCloseTimerRef = useRef(null);

  // Whether the user still has an unclaimed starter reward (email or club),
  // driving both the badge dot and the auto-open nudge. One getCreditRewardStatus
  // fetch, refreshed when a claim happens in the panel.
  const hasUnclaimedStarter = realUnclaimed;

  const refreshRealStatus = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const getStatus = httpsCallable(functions, 'getCreditRewardStatus');
      const result = await getStatus();
      setRewardStatus(result.data || null);
      setRealUnclaimed(!(result.data?.email?.claimed && result.data?.stravaClub?.claimed));
    } catch (err) {
      // Non-fatal: the dot just won't show.
    }
  }, [user?.uid]);

  useEffect(() => { refreshRealStatus(); }, [refreshRealStatus]);

  const handlePanelClaimed = () => {
    refreshRealStatus();
    if (onCreditsUpdated) onCreditsUpdated();
  };

  // Desktop: hovering the diamond wrap opens the panel; leaving schedules a
  // short-delay close so the cursor can cross the gap into the panel. Touch
  // pointers are ignored (they keep the click toggle), and the close is
  // skipped while the referral builder overlay is open since it portals to
  // document.body and would be unmounted with the panel.
  const clearHoverCloseTimer = () => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  };
  const handleDiamondPointerEnter = (e) => {
    if (e.pointerType !== 'mouse') return;
    clearHoverCloseTimer();
    setDiamondPanelOpen(true);
  };
  const handleDiamondPointerLeave = (e) => {
    if (e.pointerType !== 'mouse') return;
    clearHoverCloseTimer();
    hoverCloseTimerRef.current = setTimeout(() => {
      hoverCloseTimerRef.current = null;
      if (document.querySelector('.referral-builder-overlay')) return;
      setDiamondPanelOpen(false);
    }, 250);
  };
  useEffect(() => () => {
    if (hoverCloseTimerRef.current) clearTimeout(hoverCloseTimerRef.current);
  }, []);

  const lowBalance = typeof userCredits === 'number' && userCredits <= LOW_BALANCE_THRESHOLD;
  const diamondsTooltipDesc = lowBalance
    ? t('nav.diamondsLow', 'Low on diamonds, earn more for free')
    : t('nav.diamondsDesc', 'Earn and spend diamonds to create maps');

  // Trigger animation when credits change
  useEffect(() => {
    if (previousCredits.current !== null && userCredits !== null && previousCredits.current !== userCredits) {
      setCreditAnimation(true);
      setTimeout(() => setCreditAnimation(false), 600);
    }
    previousCredits.current = userCredits;
  }, [userCredits]);

  // Close the mobile Explore sheet + diamonds panel whenever the route changes.
  useEffect(() => {
    setExploreMenuOpen(false);
    setDiamondPanelOpen(false);
  }, [location.pathname]);

  // Close the diamonds panel on Escape or an outside click. Both the desktop
  // popover and the mobile sheet carry .diamonds-panel, and both triggers carry
  // .nav-diamond-trigger, so a class check covers every surface (and avoids the
  // desktop ref wrongly matching the mobile sheet, which lives elsewhere).
  // The referral invite builder opens FROM the panel but portals to document.body,
  // so it also counts as "inside": closing the panel would unmount the builder
  // mid-interaction.
  useEffect(() => {
    if (!diamondPanelOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('.referral-builder-overlay')) return;
      setDiamondPanelOpen(false);
    };
    const onDown = (e) => {
      const node = e.target;
      if (node?.closest && (
        node.closest('.diamonds-panel')
        || node.closest('.nav-diamond-trigger')
        || node.closest('.referral-builder-overlay')
      )) return;
      setDiamondPanelOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [diamondPanelOpen]);

  // Auto-open the panel once for a brand-new signup who still has a starter
  // reward to claim. StrictMode-guarded; gated on a per-uid localStorage flag.
  useEffect(() => {
    if (!user?.uid || autoOpenCheckedRef.current) return;
    autoOpenCheckedRef.current = true;
    (async () => {
      try {
        if (typeof window === 'undefined') return;
        const key = DIAMOND_PANEL_AUTOOPEN_KEY(user.uid);
        if (window.localStorage.getItem(key)) return;
        const snap = await getDoc(doc(db, 'users', user.uid));
        const data = snap.exists() ? snap.data() : {};
        const createdAtMs = typeof data?.createdAt?.toMillis === 'function' ? data.createdAt.toMillis() : null;
        if (createdAtMs === null || (Date.now() - createdAtMs) >= BRAND_NEW_SUPPRESS_MS) return;
        // Fetch directly: realUnclaimed state may not be loaded yet at mount.
        const getStatus = httpsCallable(functions, 'getCreditRewardStatus');
        const result = await getStatus();
        const unclaimed = !(result.data?.email?.claimed && result.data?.stravaClub?.claimed);
        if (!unclaimed) return;
        window.localStorage.setItem(key, '1');
        setTimeout(() => {
          if (typeof document === 'undefined' || document.visibilityState === 'visible') {
            setDiamondPanelOpen(true);
          }
        }, 1200);
      } catch (err) {
        // Non-fatal: just skip the auto-open.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // The Explore group: the three product landing pages, for members who want to
  // discover what else Mythic Maps does. The feed lives on Home, Create is a
  // top-level button, so neither belongs here.
  const exploreChildren = [
    { name: t('nav.fixIndoor', 'Fix indoor rides'), href: '/fix', icon: IconBike, key: 'fix', desc: t('nav.fixIndoorDesc', 'Put your Zwift rides on the real world map') },
    { name: t('nav.artStyles', 'Art styles'), href: '/maps', icon: IconPalette, key: 'maps', desc: t('nav.mapsDesc', 'Explore the 40+ art styles') },
    { name: t('nav.challenge', 'Challenges'), href: '/challenge', icon: IconTrophy, key: 'challenge', desc: t('nav.challengeDesc', 'Join challenges and compete with friends') },
  ];

  const handleNavClick = (e, href, itemKey) => {
    if (itemKey === 'create' && user && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(CREATE_PAGE_REFRESH_ACTIVITIES_EVENT));
    }
    if (!user && href !== '/' && href !== '/art') {
      e.preventDefault();
      if (onLoginRequired) {
        onLoginRequired();
      }
    }
  };

  const profileHref = user ? `/profile/${user.uid}` : '/profile/me';

  const isFixActive = location.pathname === '/fix';
  const isFeedActive = location.pathname === '/art';
  const isExploreActive = ['/fix', '/maps', '/challenge'].some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
  );
  const isCreateActive = location.pathname === '/create' || location.pathname.startsWith('/create/');
  const isProfileActive = location.pathname.startsWith('/profile');

  const isActive = (href) => {
    if (href.startsWith('/profile')) {
      return location.pathname.startsWith('/profile');
    }
    if (href === '/challenge') {
      return location.pathname.startsWith('/challenge');
    }
    if (href === '/create') {
      return location.pathname === '/create' || location.pathname.startsWith('/create/');
    }
    return location.pathname === href;
  };

  const renderExploreMenu = () => (
    <div className="nav-dropdown-menu" role="menu">
      {exploreChildren.map((child) => {
        const ChildIcon = child.icon;
        return (
          <Link
            key={child.key}
            to={child.href}
            role="menuitem"
            className={`nav-dropdown-item ${isActive(child.href) ? 'active' : ''}`}
            onClick={(e) => handleNavClick(e, child.href, child.key)}
          >
            <span className="nav-dropdown-item-icon"><ChildIcon size={18} /></span>
            <span className="nav-dropdown-item-text">
              <span className="nav-dropdown-item-name">{child.name}</span>
              <span className="nav-dropdown-item-desc">{child.desc}</span>
            </span>
          </Link>
        );
      })}
    </div>
  );

  const renderProfileMenu = () => (
    <div className="nav-dropdown-menu" role="menu">
      <Link
        to={profileHref}
        role="menuitem"
        className={`nav-dropdown-item ${isProfileActive ? 'active' : ''}`}
        onClick={(e) => handleNavClick(e, profileHref, 'profile')}
      >
        <span className="nav-dropdown-item-icon"><IconUser size={18} /></span>
        <span className="nav-dropdown-item-text">
          <span className="nav-dropdown-item-name">{t('nav.profile', 'Profile')}</span>
          <span className="nav-dropdown-item-desc">{t('nav.profileDesc', 'View your profile and generated maps')}</span>
        </span>
      </Link>
      <Link
        to="/settings"
        role="menuitem"
        className={`nav-dropdown-item ${location.pathname === '/settings' ? 'active' : ''}`}
        onClick={(e) => handleNavClick(e, '/settings', 'settings')}
      >
        <span className="nav-dropdown-item-icon"><IconSettings size={18} /></span>
        <span className="nav-dropdown-item-text">
          <span className="nav-dropdown-item-name">{t('nav.settings', 'Settings')}</span>
          <span className="nav-dropdown-item-desc">{t('nav.settingsDesc', 'Auto-upload, account and preferences')}</span>
        </span>
      </Link>
      {isAdmin && (
        <Link
          to="/admin-menu"
          role="menuitem"
          className={`nav-dropdown-item ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
          onClick={(e) => handleNavClick(e, '/admin-menu', 'admin')}
        >
          <span className="nav-dropdown-item-icon"><IconShieldCog size={18} /></span>
          <span className="nav-dropdown-item-text">
            <span className="nav-dropdown-item-name">{t('nav.admin', 'Admin')}</span>
            <span className="nav-dropdown-item-desc">{t('nav.adminDesc', 'Dashboards and admin tools')}</span>
          </span>
        </Link>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile Header */}
      <header className="navbar-mobile-header">
        <Link to={user ? '/art' : '/'} className="navbar-mobile-logo">
          <img src={MythicLogo} alt="Mythic Maps" className="navbar-mobile-logo-img" />
          <span className="navbar-mobile-logo-text">Mythic Maps</span>
        </Link>
      </header>

      {/* Desktop Navigation */}
      <nav className="navbar-desktop">
        <div className="navbar-container">
          <div className="navbar-content">
            {/* Logo: members land on the feed, visitors on the umbrella homepage */}
            <Link to={user ? '/art' : '/'} className="navbar-logo">
              <img src={MythicLogo} alt="Mythic Maps" className="navbar-logo-img" />
              <span className="navbar-logo-text">Mythic Maps</span>
            </Link>

            {/* Navigation Icons (right side) */}
            <div className="navbar-icons">
              {!user ? (
                // Logged-out: the three products (home stays on the logo), then Strava CTA
                <>
                  <Link
                    to="/fix"
                    className={`nav-text-link ${isFixActive ? 'active' : ''}`}
                    onClick={() => posthog?.capture('nav_loggedout_fix_clicked')}
                  >
                    {t('nav.fixLong', 'Fix my Zwift map')}
                  </Link>
                  <Link
                    to="/maps"
                    className={`nav-text-link ${location.pathname === '/maps' ? 'active' : ''}`}
                    onClick={() => posthog?.capture('nav_loggedout_artmaps_clicked')}
                  >
                    {t('nav.artMaps', 'Art Maps')}
                  </Link>
                  <Link
                    to="/challenge"
                    className={`nav-text-link ${location.pathname.startsWith('/challenge') ? 'active' : ''}`}
                    onClick={() => posthog?.capture('nav_loggedout_challenges_clicked')}
                  >
                    {t('nav.artChallenges', 'Challenges')}
                  </Link>
                  <button className="nav-login-button" onClick={onLoginRequired}>
                    <IconUser size={20} />
                    <span>{t('nav.connectWithStrava', 'Connect with Strava')}</span>
                  </button>
                </>
              ) : (
                <>
                  {/* Feed (home) */}
                  <Link
                    to="/art"
                    className={`nav-icon ${isFeedActive ? 'active' : ''}`}
                    onClick={(e) => handleNavClick(e, '/art', 'feed')}
                  >
                    <IconMap2 size={24} />
                    <div className="nav-tooltip">
                      <span className="nav-tooltip-title">{t('nav.feed', 'Feed')}</span>
                      <span className="nav-tooltip-desc">{t('nav.feedDesc', 'The community gallery of Mythic Maps')}</span>
                    </div>
                  </Link>

                  {/* Create: the primary action, icon-sized like its neighbours but filled */}
                  <Link
                    to="/create"
                    className={`nav-icon nav-create-icon ${isCreateActive ? 'active' : ''}`}
                    onClick={(e) => handleNavClick(e, '/create', 'create')}
                  >
                    <span className="nav-create-icon-circle"><IconPlus size={24} /></span>
                    <div className="nav-tooltip">
                      <span className="nav-tooltip-title">{t('nav.create', 'Create')}</span>
                      <span className="nav-tooltip-desc">{t('nav.createDesc', 'Turn a Strava activity into a stylised map')}</span>
                    </div>
                  </Link>

                  {/* Explore: the three product landing pages */}
                  <div className="nav-dropdown">
                    <button
                      type="button"
                      className={`nav-icon nav-dropdown-trigger ${isExploreActive ? 'active' : ''}`}
                      aria-haspopup="menu"
                      aria-label={t('nav.explore', 'Explore')}
                    >
                      <IconCompass size={24} />
                    </button>
                    {renderExploreMenu()}
                  </div>

                  {/* Diamonds (opens the compact panel on hover or click instead of navigating) */}
                  <div
                    className="nav-diamond-wrap"
                    onPointerEnter={handleDiamondPointerEnter}
                    onPointerLeave={handleDiamondPointerLeave}
                  >
                    <button
                      type="button"
                      className={`nav-icon nav-icon-diamonds nav-diamond-trigger ${diamondPanelOpen ? 'active' : ''}`}
                      onClick={() => setDiamondPanelOpen((o) => !o)}
                      aria-haspopup="dialog"
                      aria-expanded={diamondPanelOpen}
                    >
                      <IconDiamond size={24} />
                      <span className={`nav-diamond-badge ${creditAnimation ? 'animate' : ''} ${lowBalance ? 'low' : ''}`}>
                        {isLoadingCredits ? '…' : (userCredits ?? '—')}
                      </span>
                      {hasUnclaimedStarter && <span className="nav-diamond-dot" />}
                    </button>
                    {diamondPanelOpen && (
                      <DiamondsPanel
                        variant="desktop"
                        user={user}
                        userCredits={userCredits}
                        rewardStatus={rewardStatus}
                        subtitle={diamondsTooltipDesc}
                        onClose={() => setDiamondPanelOpen(false)}
                        onClaimed={handlePanelClaimed}
                      />
                    )}
                  </div>

                  {/* You: avatar navigates to profile; hover/focus reveals Profile + Settings */}
                  <div className="nav-dropdown nav-profile-dropdown">
                    <Link
                      to={profileHref}
                      className={`nav-icon nav-dropdown-trigger ${isProfileActive ? 'active' : ''}`}
                      onClick={(e) => handleNavClick(e, profileHref, 'profile')}
                    >
                      <StableAvatar
                        identityKey={user.uid}
                        src={user.photoURL || '/default-avatar.png'}
                        alt={user.displayName || 'Profile'}
                        className="nav-avatar-img"
                      />
                    </Link>
                    {renderProfileMenu()}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Navigation */}
      <nav className="navbar-mobile">
        {user && exploreMenuOpen && (
          <>
            <div className="nav-explore-mobile-backdrop" onClick={() => setExploreMenuOpen(false)} />
            <div className="nav-explore-mobile-sheet" role="menu">
              {exploreChildren.map((child) => {
                const ChildIcon = child.icon;
                return (
                  <Link
                    key={child.key}
                    to={child.href}
                    role="menuitem"
                    className={`nav-explore-mobile-item ${isActive(child.href) ? 'active' : ''}`}
                    onClick={(e) => { handleNavClick(e, child.href, child.key); setExploreMenuOpen(false); }}
                  >
                    <span className="nav-explore-mobile-item-icon"><ChildIcon size={20} /></span>
                    <span className="nav-explore-mobile-item-text">
                      <span className="nav-explore-mobile-item-name">{child.name}</span>
                      <span className="nav-explore-mobile-item-desc">{child.desc}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </>
        )}
        {user && diamondPanelOpen && (
          <>
            <div className="nav-diamond-mobile-backdrop" onClick={() => setDiamondPanelOpen(false)} />
            <div className="nav-diamond-mobile-sheet">
              <DiamondsPanel
                variant="mobile"
                user={user}
                userCredits={userCredits}
                rewardStatus={rewardStatus}
                subtitle={diamondsTooltipDesc}
                onClose={() => setDiamondPanelOpen(false)}
                onClaimed={handlePanelClaimed}
              />
            </div>
          </>
        )}
        <div className="navbar-mobile-content">
          {!user ? (
            // Logged-out: shortcuts row + full-width Strava CTA
            <div className="navbar-mobile-items navbar-mobile-items-logged-out">
              <div className="navbar-mobile-loggedout-shortcuts">
                <Link
                  to="/fix"
                  className={`mobile-loggedout-shortcut ${isFixActive ? 'active' : ''}`}
                  onClick={() => posthog?.capture('nav_loggedout_fix_clicked')}
                >
                  <IconBike size={18} />
                  <span>{t('nav.fix', 'Fix')}</span>
                </Link>
                <Link
                  to="/maps"
                  className={`mobile-loggedout-shortcut ${location.pathname === '/maps' ? 'active' : ''}`}
                  onClick={() => posthog?.capture('nav_loggedout_artmaps_clicked')}
                >
                  <IconWorld size={18} />
                  <span>{t('nav.artMapsShort', 'Art Maps')}</span>
                </Link>
                <Link
                  to="/challenge"
                  className={`mobile-loggedout-shortcut ${location.pathname.startsWith('/challenge') ? 'active' : ''}`}
                  onClick={() => posthog?.capture('nav_loggedout_challenges_clicked')}
                >
                  <IconTrophy size={18} />
                  <span>{t('nav.artChallengesShort', 'Challenges')}</span>
                </Link>
              </div>
              <button className="mobile-login-btn" onClick={onLoginRequired}>
                <IconUser size={20} />
                <span>{t('nav.connectWithStrava', 'Connect with Strava')}</span>
              </button>
            </div>
          ) : (
            // Logged-in: Feed, Explore (sheet), Create (center), Diamonds, Profile
            <div className="navbar-mobile-items">
              <Link
                to="/art"
                className={`nav-icon-mobile ${isFeedActive ? 'active' : ''}`}
                onClick={(e) => handleNavClick(e, '/art', 'feed')}
              >
                <IconMap2 size={24} />
                <span className="nav-icon-text">{t('nav.feed', 'Feed')}</span>
              </Link>
              <button
                type="button"
                className={`nav-icon-mobile nav-explore-mobile-trigger ${isExploreActive || exploreMenuOpen ? 'active' : ''}`}
                onClick={() => setExploreMenuOpen((o) => !o)}
                aria-haspopup="true"
                aria-expanded={exploreMenuOpen}
              >
                <IconCompass size={24} />
                <span className="nav-icon-text">{t('nav.explore', 'Explore')}</span>
              </button>
              <Link
                to="/create"
                className={`nav-icon-mobile nav-create-mobile ${isCreateActive ? 'active' : ''}`}
                onClick={(e) => handleNavClick(e, '/create', 'create')}
              >
                <span className="nav-create-mobile-circle"><IconPlus size={26} /></span>
                <span className="nav-icon-text">{t('nav.create', 'Create')}</span>
              </Link>
              <button
                type="button"
                className={`nav-icon-mobile nav-icon-diamonds nav-diamond-trigger ${diamondPanelOpen ? 'active' : ''}`}
                onClick={() => setDiamondPanelOpen((o) => !o)}
                aria-haspopup="dialog"
                aria-expanded={diamondPanelOpen}
              >
                <IconDiamond size={24} />
                <span className={`nav-diamond-badge ${creditAnimation ? 'animate' : ''} ${lowBalance ? 'low' : ''}`}>
                  {isLoadingCredits ? '…' : (userCredits ?? '—')}
                </span>
                {hasUnclaimedStarter && <span className="nav-diamond-dot" />}
              </button>
              <Link
                to={profileHref}
                className={`nav-icon-mobile ${isProfileActive ? 'active' : ''}`}
                onClick={(e) => handleNavClick(e, profileHref, 'profile')}
              >
                <StableAvatar
                  identityKey={user?.uid}
                  src={user?.photoURL || '/default-avatar.png'}
                  alt={user?.displayName || 'Profile'}
                  className="nav-avatar-img-mobile"
                />
                <span className="nav-icon-text">{t('nav.profile', 'Profile')}</span>
              </Link>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default NavigationBar;
